import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AtSign, Loader2, LogOut } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useRole } from '../../contexts/RoleContext';

interface PendingTeam {
  id: string;
  name: string;
}

const validateUsername = (username: string): string | null => {
  if (!username) return 'Username is required';
  if (username.length < 3) return 'Username must be at least 3 characters';
  if (username.length > 30) return 'Username must be 30 characters or less';
  if (!/^[a-z]/.test(username)) return 'Username must start with a letter';
  if (!/^[a-z][a-z0-9_]*$/.test(username)) return 'Only lowercase letters, numbers, and underscores allowed';
  return null;
};

export function RequireTeamUsernameModal() {
  const location = useLocation();
  const { user, isAuthenticated, signOut } = useAuth();
  const { refreshContexts } = useRole();
  const [queue, setQueue] = useState<PendingTeam[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [username, setUsername] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [saving, setSaving] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const hasCheckedRef = useRef(false);
  const lastUserIdRef = useRef<string | null>(null);

  const loadMissingUsernameTeams = useCallback(async () => {
    if (!user) return;

    try {
      const [ownedRes, membersRes] = await Promise.all([
        supabase
          .from('teams')
          .select('id, name')
          .eq('coach_id', user.id)
          .is('username', null),
        supabase
          .from('team_members')
          .select('teams:team_id ( id, name, username )')
          .eq('user_id', user.id)
          .eq('role', 'head_coach'),
      ]);

      const merged = new Map<string, PendingTeam>();

      (ownedRes.data || []).forEach((t) => {
        if (t?.id && t?.name) merged.set(t.id, { id: t.id, name: t.name });
      });

      (membersRes.data || []).forEach((row: { teams: { id: string; name: string; username: string | null } | null }) => {
        const team = row.teams;
        if (team && team.username === null && !merged.has(team.id)) {
          merged.set(team.id, { id: team.id, name: team.name });
        }
      });

      const pending = Array.from(merged.values());
      setQueue(pending);
      setCurrentIndex(0);
      setUsername('');
      setUsernameError('');
    } catch (err) {
      console.error('Failed to check for teams missing usernames:', err);
    }
  }, [user]);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      hasCheckedRef.current = false;
      lastUserIdRef.current = null;
      setQueue([]);
      return;
    }

    if (lastUserIdRef.current !== user.id) {
      hasCheckedRef.current = false;
      lastUserIdRef.current = user.id;
    }

    if (hasCheckedRef.current) return;
    hasCheckedRef.current = true;
    loadMissingUsernameTeams();
  }, [isAuthenticated, user, loadMissingUsernameTeams]);

  const currentTeam = queue[currentIndex];

  /** Full-screen overlay would steal clicks from standalone site-admin pages (same z-index stack). */
  if (location.pathname.startsWith('/site-admin')) return null;

  if (!currentTeam) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUsernameError('');

    const validation = validateUsername(username);
    if (validation) {
      setUsernameError(validation);
      return;
    }

    setSaving(true);

    const { error: updateError } = await supabase
      .from('teams')
      .update({ username })
      .eq('id', currentTeam.id);

    if (updateError) {
      if (updateError.code === '23505' && updateError.message.toLowerCase().includes('username')) {
        setUsernameError('This username is already taken. Please choose another.');
      } else {
        setUsernameError('Failed to save username: ' + updateError.message);
      }
      setSaving(false);
      return;
    }

    const nextIndex = currentIndex + 1;
    if (nextIndex >= queue.length) {
      setQueue([]);
      setCurrentIndex(0);
      setUsername('');
      setSaving(false);
      refreshContexts();
    } else {
      setCurrentIndex(nextIndex);
      setUsername('');
      setSaving(false);
    }
  };

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
    } finally {
      setSigningOut(false);
    }
  };

  const totalTeams = queue.length;
  const progressLabel = totalTeams > 1 ? `Team ${currentIndex + 1} of ${totalTeams}` : null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="require-team-username-title"
    >
      <div className="w-full max-w-md rounded-xl bg-white dark:bg-slate-900 shadow-2xl border border-gray-200 dark:border-slate-700 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200 dark:border-slate-700 bg-gradient-to-r from-teal-600 to-teal-700 text-white">
          {progressLabel && (
            <div className="text-xs font-medium uppercase tracking-wider text-teal-100 mb-1">
              {progressLabel}
            </div>
          )}
          <h2 id="require-team-username-title" className="text-lg font-semibold">
            Set a username for {currentTeam.name}
          </h2>
          <p className="text-sm text-teal-50 mt-1">
            Team usernames are now required. Please choose a unique handle before continuing.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label
              htmlFor="require-team-username-input"
              className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1.5"
            >
              Team Username *
            </label>
            <div className="relative">
              <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                id="require-team-username-input"
                type="text"
                required
                autoFocus
                value={username}
                onChange={(e) => {
                  const value = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
                  setUsername(value);
                  setUsernameError('');
                }}
                className={`w-full pl-9 pr-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors text-sm bg-white dark:bg-slate-800 text-gray-900 dark:text-white ${
                  usernameError
                    ? 'border-red-300 dark:border-red-500'
                    : 'border-gray-300 dark:border-slate-600'
                }`}
                placeholder="your_team_name"
                maxLength={30}
                disabled={saving || signingOut}
              />
            </div>
            {usernameError ? (
              <p className="text-xs text-red-500 mt-1">{usernameError}</p>
            ) : (
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                Lowercase letters, numbers, and underscores. 3-30 characters.
              </p>
            )}
          </div>

          <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
            <button
              type="button"
              onClick={handleSignOut}
              disabled={saving || signingOut}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {signingOut ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Signing out...
                </>
              ) : (
                <>
                  <LogOut className="w-4 h-4" />
                  Sign out
                </>
              )}
            </button>
            <button
              type="submit"
              disabled={saving || signingOut || !username || !!validateUsername(username)}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save username'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
